import { useEffect, useRef, useState } from "react"
import { api, ApiError } from "../../lib/api"
import { useSelectedAccount } from "../../contexts/AccountContext"
import PlanBadge from "../account/PlanBadge"

const getOauthQueryParam = () => {
  const params = new URLSearchParams(window.location.search)
  if (params.get("oauth_success")) return { type: "success" as const, message: "Instagramアカウントの連携が完了しました" }
  const err = params.get("oauth_error")
  if (err) return { type: "error" as const, message: `Instagram連携に失敗しました: ${err}` }
  return null
}

const clearOauthQueryParams = () => {
  const url = new URL(window.location.href)
  url.searchParams.delete("oauth_success")
  url.searchParams.delete("oauth_error")
  url.searchParams.delete("tab")
  window.history.replaceState({}, "", url.toString())
}

export type PlanFeatures = {
  all_period: boolean
  competitor: boolean
  ai: boolean
  fan_user: boolean
  category: boolean
  scheduled_post: boolean
  pptx_export: boolean
  cross_account_min_plan: "free" | "pro" | "business"
  free_window_days: number
}

export type InstagramAccount = {
  id: number
  username: string
  avatar_url: string | null
  instagram_id: string | null
  followers_count: number
  connected: boolean
  sync_status: "idle" | "syncing" | "error"
  token_status: "unknown" | "valid" | "expired" | "revoked"
  last_synced_at: string | null
  sync_error_message: string | null
  needs_reconnection: boolean
  created_at: string
  permission_level: "view" | "edit" | null
  plan: "free" | "pro" | "business"
  features: PlanFeatures
}

const CONNECTION_BADGE: Record<string, { label: string; className: string }> = {
  syncing:    { label: "接続中", className: "member-badge--yellow" },
  connected:  { label: "接続済み", className: "member-badge--green" },
  default:    { label: "未接続", className: "member-badge--red" },
}

const AccountsTab = () => {
  const { selectedOrganizationId, selectedOrganizationRole } = useSelectedAccount()
  const [accounts, setAccounts]               = useState<InstagramAccount[]>([])
  const [loading, setLoading]                 = useState(true)
  const [error, setError]                     = useState<string | null>(null)
  const [successMessage, setSuccessMessage]   = useState<string | null>(null)
  const pollingRef                            = useRef<ReturnType<typeof setInterval> | null>(null)

  const isAdmin = selectedOrganizationRole === "owner"

  const fetchAccounts = async (orgId: number | null) => {
    try {
      const params = orgId !== null ? `?organization_id=${orgId}` : ""
      const data = await api.get<InstagramAccount[]>(`/instagram_accounts${params}`)
      setAccounts(data)
      setLoading(false)
      return data
    } catch {
      setError("アカウント情報の取得に失敗しました")
      setLoading(false)
      return []
    }
  }

  const stopPolling = () => {
    if (pollingRef.current) {
      clearInterval(pollingRef.current)
      pollingRef.current = null
    }
  }

  const startPolling = (orgId: number | null) => {
    if (pollingRef.current) return
    pollingRef.current = setInterval(async () => {
      const data = await fetchAccounts(orgId)
      const hasSyncing = data.some(a => a.sync_status === "syncing")
      if (!hasSyncing) stopPolling()
    }, 5000)
  }

  useEffect(() => {
    stopPolling()
    setLoading(true)

    const oauthResult = getOauthQueryParam()
    if (oauthResult) {
      clearOauthQueryParams()
      if (oauthResult.type === "success") setSuccessMessage(oauthResult.message)
      else setError(oauthResult.message)
    }

    fetchAccounts(selectedOrganizationId).then(data => {
      if (data.some(a => a.sync_status === "syncing")) startPolling(selectedOrganizationId)
    })
    return stopPolling
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [selectedOrganizationId])

  useEffect(() => {
    const hasSyncing = accounts.some(a => a.sync_status === "syncing")
    if (hasSyncing) {
      startPolling(selectedOrganizationId)
    } else {
      stopPolling()
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [accounts])

  const orgParam = selectedOrganizationId !== null ? `?organization_id=${selectedOrganizationId}` : ""

  const handleSync = async (account: InstagramAccount) => {
    setError(null)
    setAccounts(prev => prev.map(a => a.id === account.id ? { ...a, sync_status: "syncing" } : a))
    try {
      const data = await api.post<{ message: string; account: InstagramAccount }>(
        `/instagram_accounts/${account.id}/sync${orgParam}`, {}
      )
      setAccounts(prev => prev.map(a => a.id === account.id ? data.account : a))
      setSuccessMessage(data.message)
    } catch (err) {
      const { data: errData } = err as ApiError
      setError(errData.error ?? "同期の開始に失敗しました")
      setAccounts(prev => prev.map(a => a.id === account.id ? { ...a, sync_status: "error" } : a))
    }
  }

  // 再連携は OAuth ダイアログ（/connect）を経由する。fb_exchange_token では
  // 既存トークンの権限しか引き継げず、後から追加したスコープ（pages_manage_metadata 等）を
  // 取得できないため、必ず OAuth 認可フローを通す。
  const handleReconnect = async (account: InstagramAccount) => {
    setError(null)
    try {
      const data = await api.get<{ oauth_url: string }>(`/instagram_accounts/${account.id}/connect`)
      window.location.href = data.oauth_url
    } catch (err) {
      const { data: errData } = err as ApiError
      setError(errData.error ?? "再連携URLの取得に失敗しました")
    }
  }

  const handleDelete = async (account: InstagramAccount) => {
    if (!confirm(`@${account.username} を削除してよろしいですか？`)) return
    setError(null)
    try {
      await api.delete(`/instagram_accounts/${account.id}${orgParam}`)
      setAccounts(prev => prev.filter(a => a.id !== account.id))
      setSuccessMessage(`@${account.username} を削除しました`)
    } catch (err) {
      const { data: errData } = err as ApiError
      setError(errData.error ?? "削除に失敗しました")
    }
  }

  const handleConnect = async (account: InstagramAccount) => {
    setError(null)
    try {
      const data = await api.get<{ oauth_url: string }>(`/instagram_accounts/${account.id}/connect`)
      window.location.href = data.oauth_url
    } catch (err) {
      const { data: errData } = err as ApiError
      setError(errData.error ?? "OAuth連携URLの取得に失敗しました")
    }
  }

  const handleAddAccount = async () => {
    setError(null)
    try {
      const url = selectedOrganizationId
        ? `/instagram_accounts/oauth_start?organization_id=${selectedOrganizationId}`
        : "/instagram_accounts/oauth_start"
      const data = await api.get<{ oauth_url: string }>(url)
      window.location.href = data.oauth_url
    } catch (err) {
      const { data: errData } = err as ApiError
      setError(errData.error ?? "OAuth連携URLの取得に失敗しました")
    }
  }

  const connectionBadge = (account: InstagramAccount) => {
    const key = account.sync_status === "syncing"
      ? "syncing"
      : account.connected && account.token_status === "valid"
        ? "connected"
        : "default"
    const badge = CONNECTION_BADGE[key]
    return <span className={`member-badge ${badge.className}`}>{badge.label}</span>
  }

  return (
    <div className="settings-card">
      <div className="settings-card__header">
        <i className="bi bi-instagram" />
        <h2>アカウント管理</h2>
        <span className="add-account-info">
          <i className="bi bi-info-circle" />
          <span className="add-account-info__tooltip">
            ビジネスポートフォリオ・Instagramアカウント・Facebookアカウント・Facebookページをすべて正しく連携してください。同名アカウントが複数ある場合は連携先を間違えやすいためご注意ください。
          </span>
        </span>
        {isAdmin && (
          <button className="btn-invite" onClick={handleAddAccount}>
            <i className="bi bi-plus-circle" />
            アカウントを追加
          </button>
        )}
      </div>

      <div className="settings-card__body settings-card__body--flush">
        {(error || successMessage) && (
          <div className="members-alerts">
            {error        && <div className="settings-alert settings-alert--error">{error}</div>}
            {successMessage && <div className="settings-alert settings-alert--success">{successMessage}</div>}
          </div>
        )}

        {loading ? (
          <p className="members-loading">読み込み中...</p>
        ) : accounts.length === 0 ? (
          <p className="members-loading">まだアカウントが登録されていません</p>
        ) : (
          <div className="members-table-wrapper">
            <table className="members-table">
              <thead>
                <tr>
                  <th>ユーザー名</th>
                  <th>プラン</th>
                  <th>フォロワー数</th>
                  <th>連携状態</th>
                  <th>登録日</th>
                  {isAdmin && <th className="members-table__actions-col">操作</th>}
                </tr>
              </thead>
              <tbody>
                {accounts.map(account => (
                  <tr key={account.id}>
                    <td>
                      <div className="member-name-cell">
                        <div className="member-avatar">
                          {account.avatar_url
                            ? <img src={account.avatar_url} alt={account.username} />
                            : <span><i className="bi bi-instagram" /></span>
                          }
                        </div>
                        <span className="member-name-cell__name">@{account.username}</span>
                      </div>
                    </td>
                    <td><PlanBadge plan={account.plan} /></td>
                    <td>{account.followers_count.toLocaleString()}</td>
                    <td>{connectionBadge(account)}</td>
                    <td className="members-table__date">{account.created_at}</td>
                    {isAdmin && (
                      <td className="members-table__actions">
                        <button
                          className="btn-icon"
                          title="同期"
                          onClick={() => handleSync(account)}
                          disabled={account.sync_status === "syncing"}
                        >
                          <i className={`bi bi-arrow-clockwise${account.sync_status === "syncing" ? " spin" : ""}`} />
                        </button>
                        {!account.connected && !account.needs_reconnection ? (
                          <button
                            className="btn-icon"
                            title="Instagramと連携"
                            onClick={() => handleConnect(account)}
                          >
                            <i className="bi bi-link-45deg" />
                          </button>
                        ) : (
                          <button
                            className="btn-icon"
                            title="再連携"
                            onClick={() => handleReconnect(account)}
                          >
                            <i className="bi bi-plug" />
                          </button>
                        )}
                        <button
                          className="btn-icon btn-icon--danger"
                          title="削除"
                          onClick={() => handleDelete(account)}
                        >
                          <i className="bi bi-trash" />
                        </button>
                      </td>
                    )}
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </div>

    </div>
  )
}

export default AccountsTab
